import { crumb } from "./errors";
import { type Target, currentRoute, describe, sectionInView } from "./identify";
import { Recorder, type Recording } from "./recorder";
import { STYLES } from "./styles";

// The feedback widget: a launcher of two joined bubbles, Feedback and a microphone, that opens
// a floating composer bar where the launcher sits. Feedback opens it to point and type; the
// microphone opens it already recording. Testers type and/or talk in the bar. While it is open, clicking or tapping the page tags that element instead of
// activating it; while they talk, each tag is stamped with a time offset (and each section
// scrolled into view is noted), so the transcript can say *what* "this" was.

export type Mark = { t: number; kind: "point" | "view"; label: string; target?: Target };

export type WidgetOptions = {
  endpoint: string;
  key: string;
  attributes: string[];
  consentText: string;
  /** Capture the visible screen when feedback is sent. */
  screenshots: boolean;
  /** Key that opens the bar and starts talking, or false to turn it off. */
  hotkey: string | false;
  context: () => Record<string, unknown>;
  reporter: () => string;
  setReporter: (name: string) => void;
};

const svg = (body: string, size = 16) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
const ICON = svg(`<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>`);
const MIC = svg(`<rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 19v3"/>`);
const STOP = svg(`<rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none"/>`);
const SEND = svg(`<path d="M12 19V5M5 12l7-7 7 7"/>`);
const CLIP = svg(`<path d="m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5"/>`);
const CLOSE = svg(`<path d="M18 6 6 18M6 6l12 12"/>`, 14);
const MINIMIZE = svg(`<path d="m6 9 6 6 6-6"/>`, 16);
const PIN = svg(`<path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12z"/><circle cx="12" cy="10" r="2.5"/>`, 12);
const CONSENT_KEY = "afb:mic-consent";
const NAME_KEY = "afb:reporter-name";
/** "0" when the tester set the hotkey (F) to open the bar without recording. On unless they do. */
const VOICE_KEY = "afb:voice-on-open";
const voiceOnOpen = () => storage(VOICE_KEY) !== "0";

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, any> = {}, ...children: (Node | string | null | false)[]) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k.startsWith("on")) el.addEventListener(k.slice(2), v);
    else if (k === "html") el.innerHTML = v;
    else if (v !== undefined && v !== false) el.setAttribute(k, v === true ? "" : v);
  }
  for (const c of children) if (c) el.append(c);
  return el;
}

const short = (s: string) => (s.length > 28 ? s.slice(0, 27) + "…" : s);
const fmt = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}`;

function storage(key: string, value?: string): string | null {
  try {
    if (value !== undefined) localStorage.setItem(key, value);
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

const EDIT = svg(`<path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>`, 12);

/**
 * What testers see for an element: its own words, shortened, or "Selection 2" (the order it was
 * tagged in) when it has none, such as an image. Agents get the technical label.
 */
function friendly(t: Target | undefined, n: number): string {
  const words = (t?.ariaLabel || t?.text || "").replace(/\s+/g, " ").trim();
  if (words) return `“${words.length > 40 ? words.slice(0, 39) + "…" : words}”`;
  return `Selection ${n}`;
}

/** The element whose words a tester means: the one clicked, or a close ancestor, with up to 500 characters of text. */
function textOwner(el: Element): HTMLElement | null {
  for (let e: Element | null = el, n = 0; e && n < 3; e = e.parentElement, n++) {
    const text = (e as HTMLElement).innerText?.trim();
    if (text) return text.length <= 500 ? (e as HTMLElement) : null;
  }
  return null;
}

/** The single text node of an element, when it has exactly one: then a suggestion can be previewed in place. */
function singleTextNode(el: Element): Text | null {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP) });
  const first = walker.nextNode() as Text | null;
  return first && !walker.nextNode() ? first : null;
}

/** Phones and tablets: no hover, no keyboard shortcuts, "tap" instead of "click". */
const touch = () => matchMedia("(hover: none) and (pointer: coarse)").matches;

type Position = { x: number; y: number };

/** A remembered position, stored as a fraction of the viewport so it survives resizes. */
function savedPosition(key: string): Position | null {
  try {
    const p = JSON.parse(storage(key) ?? "null");
    return typeof p?.x === "number" && typeof p?.y === "number" ? p : null;
  } catch {
    return null;
  }
}

/** Put an element at a remembered position, kept fully on screen. */
function place(el: HTMLElement, pos: Position) {
  const r = el.getBoundingClientRect();
  const x = Math.min(Math.max(8, pos.x * innerWidth - r.width / 2), innerWidth - r.width - 8);
  const y = Math.min(Math.max(8, pos.y * innerHeight - r.height / 2), innerHeight - r.height - 8);
  Object.assign(el.style, { left: `${x}px`, top: `${y}px`, right: "auto", bottom: "auto", transform: "none" });
}

/**
 * Make `el` draggable by `handle`. Returns whether the last press was a drag, so a click that
 * ends a drag can be ignored. `onEnd` stores the new position.
 */
function draggable(el: HTMLElement, handle: HTMLElement, onEnd: () => void, onMove?: () => void) {
  const state = { dragged: false };
  handle.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    const start = { x: e.clientX, y: e.clientY };
    const r = el.getBoundingClientRect();
    const offset = { x: e.clientX - r.left, y: e.clientY - r.top };
    const move = (ev: PointerEvent) => {
      if (!state.dragged && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 5) return;
      ev.preventDefault();
      state.dragged = true;
      el.classList.add("dragging");
      const x = Math.min(Math.max(8, ev.clientX - offset.x), innerWidth - r.width - 8);
      const y = Math.min(Math.max(8, ev.clientY - offset.y), innerHeight - r.height - 8);
      Object.assign(el.style, { left: `${x}px`, top: `${y}px`, right: "auto", bottom: "auto", transform: "none" });
      onMove?.();
    };
    const up = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", up);
      el.classList.remove("dragging");
      if (!state.dragged) return;
      // The click that ends a drag fires right after this; ignore it, then re-arm.
      setTimeout(() => (state.dragged = false), 0);
      onEnd();
    };
    addEventListener("pointermove", move, { passive: false });
    addEventListener("pointerup", up);
  });
  return state;
}

export function mountWidget(opts: WidgetOptions) {
  const host = document.createElement("agentic-feedback");
  const root = host.attachShadow({ mode: "open" });
  root.append(h("style", { html: STYLES }));

  const fab = h("button", { class: "fab", type: "button", "aria-label": "Give feedback", title: "Point at the page and type", html: `${ICON}<span>Feedback</span>` });
  const micFab = h("button", {
    class: "fab mic-fab",
    type: "button",
    "aria-label": "Talk: record a voice note",
    title: opts.hotkey ? `Talk (${opts.hotkey.toUpperCase()})` : "Talk",
    html: MIC,
  });
  /** Two joined bubbles that move together; the bar opens where they sit. */
  const launcher = h("div", { class: "launcher" }, fab, micFab);
  const hl = h("div", { class: "hl", hidden: true });
  const toast = h("div", { class: "toast", hidden: true, role: "status" });

  // ---------------------------------------------------------------------------
  // The composer bar. Built once and updated in place, so the text field keeps its focus,
  // caret and value while the recording timer ticks.
  // ---------------------------------------------------------------------------

  const grip = h("div", { class: "grip", title: "Drag to move", "aria-hidden": "true" });
  const status = h("div", { class: "status" });
  const chips = h("div", { class: "chips" });
  const textarea = h("textarea", { rows: 1, "aria-label": "Your feedback" }) as HTMLTextAreaElement;
  const fileInput = h("input", { type: "file", accept: "image/*", multiple: true, hidden: true }) as HTMLInputElement;
  const clipBtn = h("button", { class: "icon", type: "button", "aria-label": "Attach an image", title: "Attach an image", html: CLIP });
  const micBtn = h("button", { class: "icon mic", type: "button" });
  const sendBtn = h("button", { class: "send", type: "button", "aria-label": "Send" }) as HTMLButtonElement;
  const editArea = h("textarea", { rows: 2, "aria-label": "New text" }) as HTMLTextAreaElement;
  /** Flips the page between the original text and the suggestion. */
  const previewBtn = h("button", { type: "button", class: "small toggle", onclick: () => togglePreview() }) as HTMLButtonElement;
  const editBox = h(
    "div",
    { class: "edit", hidden: true },
    editArea,
    h("div", { class: "row" },
      previewBtn,
      h("span", { class: "spacer" }),
      h("button", { type: "button", class: "small", onclick: () => cancelEdit() }, "Cancel"),
      h("button", { type: "button", class: "small primary", onclick: () => finishEdit() }, "Done"),
    ),
  );
  const inputRow = h("div", { class: "input" }, textarea, fileInput, clipBtn, micBtn, sendBtn);
  const nameBtn = h("button", { class: "link", type: "button" });
  const nameInput = h("input", { class: "name", placeholder: "Your name", "aria-label": "Your name", autocomplete: "name" }) as HTMLInputElement;
  const footNote = h("span", { class: "note" });
  /** Whether the hotkey (F) opens the bar already recording. Desktop only: phones have no hotkey. */
  const voiceToggle = h("button", { type: "button", class: "voice-toggle", role: "switch" }) as HTMLButtonElement;
  const closeBtn = h("button", { class: "link close", type: "button", "aria-label": "Close", html: `${CLOSE}<span></span>` });
  const bar = h(
    "div",
    { class: "bar", hidden: true, role: "dialog", "aria-label": "Feedback" },
    grip,
    status,
    chips,
    editBox,
    inputRow,
    h("div", { class: "foot" }, nameBtn, nameInput, voiceToggle, footNote, closeBtn),
  );
  /** Phones: the open bar folds into this one pulsing button, so the page stays free to tap. */
  const mini = h("button", { class: "mini", type: "button", hidden: true });
  const minBtn = h("button", { class: "link minimize", type: "button", "aria-label": "Minimize", html: `${MINIMIZE}<span>Minimize</span>` });
  bar.querySelector(".foot")!.insertBefore(minBtn, closeBtn);
  root.append(hl, bar, mini, toast, launcher);
  document.body.append(host);

  const recorder = new Recorder();
  let open = false;
  /** Phones only: the bar is folded into the pulsing button. */
  let collapsed = false;
  /** Folding waits for the one-time voice consent, which needs the full bar. */
  let collapseAfterConsent = false;
  let comment = "";
  /** Elements tagged while not recording; the first one is the issue's element. */
  let tags: { target: Target; el: Element }[] = [];
  /** Elements tagged and sections viewed while recording, with time offsets. */
  let marks: Mark[] = [];
  let recording: Recording | null = null;
  let askingConsent = false;
  let editingName = false;
  let sending = false;
  let sendStage = "";
  let selectedText = "";
  let tick: ReturnType<typeof setInterval> | undefined;
  let lastSection: string | null = null;
  /** Elements to outline on the screenshot. */
  let pointedEls: Element[] = [];
  let attachments: { blob: Blob; url: string }[] = [];
  /** Suggested wording changes. `node` is previewed in place and restored when the bar closes. */
  // `range` is the selected part of `node` when only part of the text is being reworded.
  type TextEdit = { target: Target; el: HTMLElement; before: string; after: string; node: Text | null; original: string | null; range: [number, number] | null };
  let textEdits: TextEdit[] = [];
  /** The edit open in the bar, if any; `previous` is the node's text when it opened, for Cancel. */
  // `kept` is the saved change being adjusted, put back as it was on Cancel.
  let editing: (Omit<TextEdit, "after"> & { previous: string | null; kept?: TextEdit }) | null = null;
  /** The page shows the suggestion while editing (on by default); off shows the original to compare. */
  let previewOn = true;
  const MAX_ATTACHMENTS = 3;
  const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

  const isOwn = (e: Event) => e.composedPath().includes(host);
  const pointing = () => open && !sending;
  const canRecord = () => Recorder.supported() && Recorder.allowedByPolicy();

  // ---------------------------------------------------------------------------
  // Pointing: hover highlight and click capture on the host page while the bar is open
  // ---------------------------------------------------------------------------

  function highlight(el: Element | null, flash = false) {
    if (!el) return void (hl.hidden = true);
    const r = el.getBoundingClientRect();
    Object.assign(hl.style, { left: `${r.left - 2}px`, top: `${r.top - 2}px`, width: `${r.width + 4}px`, height: `${r.height + 4}px` });
    hl.classList.toggle("flash", flash);
    hl.hidden = false;
  }

  function onMove(e: MouseEvent) {
    if (!pointing() || isOwn(e) || touch()) return void (pointing() || (hl.hidden = true));
    highlight(e.target as Element);
  }

  function onClick(e: MouseEvent) {
    if (isOwn(e)) return;
    if (!pointing()) {
      // Breadcrumb for error context; not a pointing action.
      const el = e.target as Element;
      if (el instanceof Element) crumb("click", describe(el, opts.attributes).label);
      return;
    }
    // While the bar is open, clicks tag elements instead of activating links and buttons. A click
    // that ends a text selection is a rewording, handled on mouseup.
    e.preventDefault();
    e.stopPropagation();
    if (pageSelection()) return;
    const el = e.target as Element;
    // With a mouse, wait a moment: the click may be the start of a double-click to reword.
    clearTimeout(pendingTag);
    if (touch()) tag(el);
    else pendingTag = setTimeout(() => tag(el), 250);
  }

  let pendingTag: ReturnType<typeof setTimeout> | undefined;

  function tag(el: Element) {
    if (!pointing()) return;
    const t = describe(el, opts.attributes);
    if (recorder.active) addMark({ t: recorder.elapsed, kind: "point", label: t.label, target: t });
    else if (!tags.some((x) => x.target.label === t.label)) tags.push({ target: t, el });
    pointedEls.push(el);
    highlight(el, true);
    setTimeout(() => hl.classList.contains("flash") && (hl.hidden = true), 900);
    render();
    // Back to typing on a keyboard; on phones the keyboard would cover the page again.
    if (!touch() && !recorder.active) textarea.focus();
  }

  // Keep the page's own press handlers (menus, drags) from firing while pointing. The default
  // action stays, so testers can still select text to reword it.
  function swallow(e: Event) {
    if (pointing() && !isOwn(e)) e.stopPropagation();
  }

  // Repeated clicks on the same thing add nothing for the reader.
  function addMark(m: Mark) {
    const last = marks[marks.length - 1];
    if (!last || last.label !== m.label || last.kind !== m.kind) marks.push(m);
  }

  addEventListener("mousemove", onMove, true);
  addEventListener("scroll", () => (hl.hidden = true), { capture: true, passive: true });
  addEventListener("click", onClick, true);
  // Double-clicking text while the bar is open goes straight to suggesting new wording.
  addEventListener(
    "dblclick",
    (e) => {
      if (!pointing() || isOwn(e)) return;
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(pendingTag);
      getSelection()?.removeAllRanges();
      startEdit(e.target as Element);
    },
    true,
  );
  // Selecting text on the page opens the edit box with that text. On a keyboard and mouse the
  // selection ends with mouseup; on phones it is made by long-press and handles, so wait for it to settle.
  addEventListener(
    "mouseup",
    (e) => {
      if (!pointing() || isOwn(e) || touch()) return;
      setTimeout(() => editSelection(), 0);
    },
    true,
  );
  let settle: ReturnType<typeof setTimeout> | undefined;
  document.addEventListener("selectionchange", () => {
    if (!pointing() || !touch()) return;
    clearTimeout(settle);
    settle = setTimeout(() => editSelection(), 700);
  });
  addEventListener("mousedown", swallow, true);
  addEventListener("pointerdown", swallow, true);
  addEventListener("keydown", (e) => {
    if (sending) return;
    if (e.key === "Escape" && open) return close();
    if (e.altKey && e.shiftKey && e.code === "KeyF") return open ? close() : openBar(voiceOnOpen());
    if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || isTyping(e)) return;
    // Enter sends a voice note while talking, when the text field doesn't have focus.
    if (e.key === "Enter" && recorder.active) {
      e.preventDefault();
      return void send();
    }
    // Bare hotkey (default F): opens the bar and starts talking, or closes it. Never while typing.
    if (!opts.hotkey || e.key.toLowerCase() !== opts.hotkey.toLowerCase()) return;
    e.preventDefault();
    open ? close() : openBar(voiceOnOpen());
  });

  // ---------------------------------------------------------------------------
  // Launcher and bar placement. Both can be dragged; their positions are remembered.
  // ---------------------------------------------------------------------------

  // One position for the whole widget: the launcher's centre. The bar opens centred on it,
  // with its bottom edge where the launcher's is, so moving either moves both.
  const POS_KEY = "afb:widget-position";
  const LAUNCHER_HALF = 20;
  const position = () => savedPosition(POS_KEY) ?? savedPosition("afb:fab-position");
  const savePosition = (cx: number, cy: number) => storage(POS_KEY, JSON.stringify({ x: cx / innerWidth, y: cy / innerHeight }));

  // Keep a text selection alive: pressing a bubble would otherwise clear it.
  launcher.addEventListener("mousedown", (e) => {
    e.preventDefault();
    selectedText = String(getSelection() ?? "").trim().slice(0, 1000);
  });
  const launcherDrag = draggable(launcher, launcher, () => {
    const r = launcher.getBoundingClientRect();
    savePosition(r.left + r.width / 2, r.top + r.height / 2);
  });
  fab.addEventListener("click", () => !launcherDrag.dragged && openBar());
  micFab.addEventListener("click", () => !launcherDrag.dragged && openBar(true));
  draggable(
    bar,
    grip,
    () => {
      const r = bar.getBoundingClientRect();
      savePosition(r.left + r.width / 2, r.bottom - LAUNCHER_HALF);
    },
    () => placeToast(),
  );

  const miniDrag = draggable(mini, mini, () => {
    const r = mini.getBoundingClientRect();
    savePosition(r.left + r.width / 2, r.top + r.height / 2);
  });
  mini.addEventListener("click", () => !miniDrag.dragged && expand());
  minBtn.addEventListener("click", () => collapse());

  /** The folded button sits where the launcher does. */
  function placeMini() {
    const pos = position() ?? { x: (innerWidth - 20 - launcherWidth / 2) / innerWidth, y: (innerHeight - 20 - LAUNCHER_HALF) / innerHeight };
    place(mini, pos);
  }

  function expand() {
    collapsed = false;
    render();
    placeBar();
  }

  function collapse() {
    if (!touch() || !open) return;
    collapsed = true;
    textarea.blur();
    render();
  }

  function placeLauncher() {
    const pos = position();
    if (pos) place(launcher, pos);
  }

  function placeBar() {
    const pos = position();
    const r = bar.getBoundingClientRect();
    // Without a saved position the launcher sits at its default spot, bottom right.
    const cx = pos ? pos.x * innerWidth : innerWidth - 20 - launcherWidth / 2;
    const bottom = pos ? pos.y * innerHeight + LAUNCHER_HALF : innerHeight - 20;
    // Lift it above the on-screen keyboard when there is one.
    const vv = window.visualViewport;
    const keyboard = vv ? Math.max(0, innerHeight - vv.height - vv.offsetTop) : 0;
    const x = Math.min(Math.max(8, cx - r.width / 2), innerWidth - r.width - 8);
    const fromBottom = Math.max(8 + keyboard, innerHeight - bottom);
    Object.assign(bar.style, { left: `${x}px`, right: "auto", top: "auto", transform: "none", bottom: `${Math.min(fromBottom, innerHeight - r.height - 8)}px` });
  }

  /** Messages appear where the bar is (or the button, when the bar is closed). */
  function placeToast() {
    const anchor = (open ? (collapsed ? mini : bar) : launcher).getBoundingClientRect();
    const below = anchor.top + anchor.height / 2 < innerHeight / 2;
    // Centred on the anchor, but kept on screen.
    // offsetWidth ignores the entrance animation's scale.
    const half = toast.offsetWidth / 2;
    Object.assign(toast.style, {
      left: `${Math.max(8 + half, Math.min(anchor.left + anchor.width / 2, innerWidth - 8 - half))}px`,
      top: below ? `${anchor.bottom + 10}px` : "auto",
      bottom: below ? "auto" : `${innerHeight - anchor.top + 10}px`,
    });
  }

  const launcherWidth = launcher.getBoundingClientRect().width || 150;
  placeLauncher();
  addEventListener("resize", () => (open ? placeBar() : placeLauncher()));
  window.visualViewport?.addEventListener("resize", () => open && placeBar());

  // ---------------------------------------------------------------------------
  // Open and close
  // ---------------------------------------------------------------------------

  /** Open the bar. `talk` starts recording at once: the mic bubble always, the hotkey when set to. */
  function openBar(talk = false) {
    talk &&= canRecord();
    const sel = getSelection();
    if (selectedText && sel?.rangeCount) {
      const node = sel.getRangeAt(0).commonAncestorContainer;
      const el = node instanceof Element ? node : node.parentElement;
      if (el) {
        tags = [{ target: describe(el, opts.attributes), el }];
        pointedEls = [el];
      }
    }
    open = true;
    // Phones start folded into a pulsing button; tapping it opens the full bar to add details and send.
    collapsed = touch();
    render();
    if (!collapsed) placeBar();
    if (talk) void toggleMic();
    else if (!touch()) textarea.focus();
    else showToast("Tap anything to point at it. Tap the pulsing button to write and send.");
  }

  function close() {
    recorder.cancel();
    clearInterval(tick);
    open = false;
    collapsed = false;
    collapseAfterConsent = false;
    comment = "";
    textarea.value = "";
    tags = [];
    marks = [];
    recording = null;
    askingConsent = false;
    editingName = false;
    selectedText = "";
    pointedEls = [];
    attachments.forEach((a) => URL.revokeObjectURL(a.url));
    attachments = [];
    if (editing) cancelEdit();
    // Newest first, so several changes to one piece of text unwind in order.
    for (const e of [...textEdits].reverse()) if (e.node && e.original !== null) e.node.nodeValue = e.original;
    textEdits = [];
    hl.hidden = true;
    render();
  }

  // ---------------------------------------------------------------------------
  // Suggesting new wording
  // ---------------------------------------------------------------------------

  /** The text selected on the page (not in the widget), if any. */
  function pageSelection(): Selection | null {
    const sel = getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount || !String(sel).trim()) return null;
    const node = sel.getRangeAt(0).commonAncestorContainer;
    return host.contains(node) || (node instanceof Element && node.closest("agentic-feedback")) ? null : sel;
  }

  /** Open the edit box with the selected text; only that part is reworded. */
  function editSelection() {
    if (editing || sending) return;
    const sel = pageSelection();
    if (!sel) return;
    const range = sel.getRangeAt(0);
    const text = String(sel).trim();
    const container = range.commonAncestorContainer;
    const el = container instanceof Element ? container : container.parentElement;
    if (!el || text.length > 500) return;
    // Within one text node the change can be previewed in place.
    const node = range.startContainer === range.endContainer && range.startContainer instanceof Text ? range.startContainer : null;
    let span: [number, number] | null = null;
    if (node) {
      const raw = node.nodeValue ?? "";
      const lead = raw.slice(range.startOffset, range.endOffset).search(/\S/);
      const start = range.startOffset + Math.max(0, lead);
      span = [start, start + text.length];
    }
    sel.removeAllRanges();
    open$({ target: describe(textOwner(el) ?? el, opts.attributes), el: (textOwner(el) ?? el) as HTMLElement, before: text, node, original: node?.nodeValue ?? null, range: span, previous: node?.nodeValue ?? null }, text);
  }

  /** Double-click: reword the whole text of the element. */
  function startEdit(clicked: Element) {
    const el = textOwner(clicked);
    if (!el) return showToast("There's no short text here to reword. Describe the change instead.", true);
    const existing = textEdits.findIndex((e) => e.el === el && !e.range);
    if (existing >= 0) return reopenEdit(existing);
    const node = singleTextNode(el);
    open$({ target: describe(el, opts.attributes), el, before: el.innerText.trim(), node, original: node?.nodeValue ?? null, range: null, previous: node?.nodeValue ?? null }, el.innerText.trim());
  }

  /** Open a saved change again, to adjust it. */
  function reopenEdit(i: number) {
    const [e] = textEdits.splice(i, 1);
    if (!e) return;
    open$({ ...e, previous: e.node?.nodeValue ?? null, kept: e }, e.after);
  }

  function open$(next: NonNullable<typeof editing>, value: string) {
    if (recorder.active) return showToast("Stop recording first, then reword the text.", true);
    if (editing) cancelEdit();
    if (collapsed) {
      collapsed = false;
      requestAnimationFrame(placeBar);
    }
    editing = next;
    editArea.value = value;
    previewOn = true;
    preview(next, value);
    pointedEls.includes(next.el) || pointedEls.push(next.el);
    render();
    editArea.focus();
    editArea.select();
  }

  /** Show the suggestion on the page: replace the selected part, or the node's text keeping its surrounding spaces. */
  function preview(e: { node: Text | null; original: string | null; range: [number, number] | null }, text: string) {
    if (!e.node || e.original === null) return;
    if (e.range) {
      e.node.nodeValue = e.original.slice(0, e.range[0]) + text + e.original.slice(e.range[1]);
      return;
    }
    const lead = e.original.match(/^\s*/)![0];
    const trail = e.original.match(/\s*$/)![0];
    e.node.nodeValue = lead + text + trail;
  }

  editArea.addEventListener("input", () => editing && previewOn && preview(editing, editArea.value));

  function togglePreview() {
    if (!editing?.node) return;
    previewOn = !previewOn;
    if (previewOn) preview(editing, editArea.value);
    else editing.node.nodeValue = editing.previous;
    render();
  }
  editArea.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing && !touch()) {
      // Enter keeps the change and sends the note in one go; Done keeps it without sending.
      e.preventDefault();
      finishEdit();
      void send();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancelEdit();
    }
  });

  function finishEdit() {
    if (!editing) return;
    const { target, el, before, node, original, range, previous } = editing;
    const after = editArea.value.trim();
    if (after && after !== before) {
      textEdits.push({ target, el, before, after, node, original, range });
      // Kept changes stay visible on the page until the bar closes.
      preview(editing, after);
    } else if (node) node.nodeValue = previous;
    editing = null;
    render();
    if (!touch()) textarea.focus();
  }

  function cancelEdit() {
    if (!editing) return;
    if (editing.node) editing.node.nodeValue = editing.previous;
    if (editing.kept) textEdits.push(editing.kept);
    editing = null;
    render();
  }

  function removeEdit(i: number) {
    const [e] = textEdits.splice(i, 1);
    if (e?.node && e.original !== null) e.node.nodeValue = e.original;
    render();
  }

  function showToast(text: string, error = false) {
    toast.textContent = text;
    toast.classList.toggle("err", error);
    toast.hidden = false;
    placeToast();
    setTimeout(() => (toast.hidden = true), 3500);
  }

  // ---------------------------------------------------------------------------
  // Voice
  // ---------------------------------------------------------------------------

  async function toggleMic() {
    if (recorder.active) return stopRecording();
    if (!canRecord()) return showToast("Voice notes are turned off on this site. Please type your feedback.", true);
    if (storage(CONSENT_KEY) !== "1") {
      askingConsent = true;
      if (collapsed) {
        collapseAfterConsent = true;
        return expand();
      }
      return render();
    }
    askingConsent = false;
    try {
      recording = null;
      marks = [];
      await recorder.start(stopRecording);
    } catch (err) {
      collapseAfterConsent = false;
      showToast(micErrorMessage(err), true);
      return render();
    }
    if (collapseAfterConsent) {
      collapseAfterConsent = false;
      collapsed = true;
    }
    lastSection = null;
    // What they tagged before talking counts as pointed at the start.
    for (const t of tags) marks.push({ t: 0, kind: "point", label: t.target.label, target: t.target });
    tags = [];
    tick = setInterval(() => {
      // Record which section is on screen as the tester scrolls while talking.
      const section = sectionInView(opts.attributes, host);
      if (section && section !== lastSection) {
        lastSection = section;
        addMark({ t: recorder.elapsed, kind: "view", label: section });
      }
      render();
    }, 1000);
    render();
  }

  async function stopRecording() {
    clearInterval(tick);
    recording = await recorder.stop();
    hl.hidden = true;
    render();
  }

  // ---------------------------------------------------------------------------
  // Bar controls
  // ---------------------------------------------------------------------------

  textarea.addEventListener("input", () => {
    comment = textarea.value;
    // Grow with the text, up to about six lines.
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
    render();
  });
  textarea.addEventListener("keydown", (e) => {
    // Enter sends on a keyboard; Shift+Enter adds a line. Phones keep Enter for new lines.
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing && !touch()) {
      e.preventDefault();
      void send();
    }
  });
  bar.addEventListener("paste", (e) => {
    const files = [...(e.clipboardData?.files ?? [])].filter((f) => f.type.startsWith("image/"));
    if (files.length) {
      e.preventDefault();
      addAttachments(files);
    }
  });
  clipBtn.addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", () => {
    addAttachments(fileInput.files ?? []);
    fileInput.value = "";
  });
  micBtn.addEventListener("click", () => void toggleMic());
  sendBtn.addEventListener("click", () => void send());
  closeBtn.addEventListener("click", close);
  voiceToggle.addEventListener("click", () => {
    storage(VOICE_KEY, voiceOnOpen() ? "0" : "1");
    render();
  });
  nameBtn.addEventListener("click", () => {
    editingName = true;
    nameInput.value = storage(NAME_KEY) ?? "";
    render();
    nameInput.focus();
  });
  const saveName = () => {
    if (!editingName) return;
    opts.setReporter(nameInput.value.trim());
    editingName = false;
    render();
  };
  nameInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveName();
    }
  });
  nameInput.addEventListener("blur", saveName);

  function addAttachments(files: Iterable<File | Blob>) {
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      if (attachments.length >= MAX_ATTACHMENTS) return showToast(`Up to ${MAX_ATTACHMENTS} images per note.`, true);
      if (f.size > MAX_ATTACHMENT_BYTES) {
        showToast("That image is over 3 MB.", true);
        continue;
      }
      attachments.push({ blob: f, url: URL.createObjectURL(f) });
    }
    render();
  }

  // ---------------------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------------------

  /**
   * Why the last screenshot is missing, sent with the report so a missing image can be explained:
   * "ok", "off", "timeout", "empty" or "failed: <reason>".
   */
  let shotStatus = "off";

  /** Screenshot of the visible page, with tagged elements outlined. Never blocks sending. */
  async function screenshot(): Promise<Blob | null> {
    if (!opts.screenshots) return (shotStatus = "off"), null;
    try {
      const mod: { captureViewport: (o: { exclude: Element; outlines: { x: number; y: number; w: number; h: number }[] }) => Promise<Blob | null> } =
        await import(/* @vite-ignore */ `${opts.endpoint}/sdk-capture.js`);
      const outlines = pointedEls
        .filter((el) => el.isConnected)
        .map((el) => {
          const r = el.getBoundingClientRect();
          return { x: r.left, y: r.top, w: r.width, h: r.height };
        });
      const TIMEOUT = Symbol();
      // Phones render more slowly; give them longer before sending without an image.
      const result = await Promise.race([
        mod.captureViewport({ exclude: host, outlines }),
        new Promise<typeof TIMEOUT>((r) => setTimeout(() => r(TIMEOUT), touch() ? 12000 : 8000)),
      ]);
      if (result === TIMEOUT) {
        shotStatus = "timeout";
        console.warn("[agentic-feedback] screenshot timed out; sending without it");
        return null;
      }
      shotStatus = result ? "ok" : "empty";
      return result;
    } catch (err) {
      shotStatus = `failed: ${String((err as Error)?.message ?? err).slice(0, 200)}`;
      console.warn("[agentic-feedback] screenshot failed", err);
      return null;
    }
  }

  const hasContent = () => !!(comment.trim() || recording || recorder.active || attachments.length || textEdits.length);

  async function send() {
    if (sending) return;
    if (recorder.active) await stopRecording();
    if (editing) finishEdit();
    if (!comment.trim() && !recording && !attachments.length && !textEdits.length) return;
    sending = true;
    hl.hidden = true;
    // The screenshot leaves the widget out, so the bar can show progress meanwhile.
    sendStage = opts.screenshots ? "Capturing…" : "Sending…";
    render();
    const shot = await screenshot();
    sendStage = "Sending…";
    render();
    // The issue's element: the first thing tagged, before or while talking.
    const firstPoint = marks.find((m) => m.kind === "point");
    const target = tags[0]?.target ?? firstPoint?.target ?? textEdits[0]?.target ?? null;
    const sentMarks: Mark[] = recording ? marks : tags.map((t) => ({ t: 0, kind: "point", label: t.target.label, target: t.target }));
    const form = new FormData();
    form.append("key", opts.key);
    form.append(
      "payload",
      JSON.stringify({
        comment: comment.trim(),
        url: location.href,
        route: currentRoute(),
        target,
        selectedText: selectedText || undefined,
        marks: sentMarks.length ? sentMarks.map(({ t, kind, label, target }) => ({ t, kind, label, target: kind === "point" ? target : undefined })) : undefined,
        audioMs: recording?.ms,
        textEdits: textEdits.length ? textEdits.map(({ target, before, after }) => ({ target, before, after })) : undefined,
        context: { ...opts.context(), screenshot: shotStatus },
        reporter: opts.reporter(),
      }),
    );
    if (recording) form.append("audio", recording.blob, "voice");
    if (shot) form.append("screenshot", shot, "screen.jpg");
    for (const a of attachments) form.append("attachment", a.blob, "attachment");
    try {
      const res = await fetch(`${opts.endpoint}/v1/feedback`, { method: "POST", body: form });
      if (!res.ok) throw new Error(String(res.status));
      sending = false;
      close();
      showToast("Thanks! Your feedback was sent.");
    } catch {
      showToast("Could not send feedback. Please try again.", true);
    } finally {
      sending = false;
      sendStage = "";
      render();
    }
  }

  // ---------------------------------------------------------------------------
  // Render: updates the bar in place.
  // ---------------------------------------------------------------------------

  function render() {
    launcher.hidden = open;
    micFab.hidden = !canRecord();
    bar.hidden = !open || collapsed;
    mini.hidden = !open || !collapsed;
    if (!open) return placeLauncher();
    if (collapsed) return renderMini();
    minBtn.hidden = !touch() || sending;
    const tap = touch() ? "Tap" : "Click";
    bar.classList.toggle("busy", sending);

    // Status line: what's happening, or how it works.
    if (editing) {
      status.replaceChildren(
        h("span", { class: "muted" }, editing.node ? `Type the new wording; the page shows it as you type.${touch() ? "" : " Enter sends it."}` : `Type the new wording.${touch() ? "" : " Enter sends it."}`),
      );
    } else if (askingConsent) {
      status.replaceChildren(
        h("span", { class: "consent" }, opts.consentText),
        h("span", { class: "row" },
          h("button", { type: "button", class: "small", onclick: () => ((askingConsent = false), render()) }, "Not now"),
          h("button", { type: "button", class: "small primary", onclick: () => (storage(CONSENT_KEY, "1"), void toggleMic()) }, "Allow and talk"),
        ),
      );
    } else if (recorder.active) {
      const pointed = marks.filter((m) => m.kind === "point").length;
      status.replaceChildren(
        h("span", { class: "rec" }, h("span", { class: "dot" }), `Listening ${fmt(recorder.elapsed)}`),
        h("span", { class: "muted" }, pointed ? `${pointed} tagged · keep pointing as you talk` : `${tap} anything you mention`),
      );
    } else if (sending) {
      status.replaceChildren(h("span", { class: "muted" }, sendStage));
    } else {
      status.replaceChildren(
        h("span", { class: "muted" }, `Type or talk. ${tap} something to point at it, or ${touch() ? "select" : "double-click or select"} text to reword it.`),
      );
    }

    // Chips: tagged elements, the voice note, attached images.
    const items: Node[] = [];
    if (selectedText) items.push(h("span", { class: "chip", title: selectedText }, `“${selectedText.slice(0, 40)}${selectedText.length > 40 ? "…" : ""}”`));
    tags.forEach((t, i) =>
      items.push(
        h("span", { class: "chip", title: t.target.label, html: PIN },
          h("span", { class: "label" }, friendly(t.target, i + 1)),
          h("button", { type: "button", class: "x", "aria-label": "Remove", onclick: () => (tags.splice(i, 1), render()) }, "×"),
        ),
      ),
    );
    textEdits.forEach((e, i) =>
      items.push(
        h("span", { class: "chip edit-chip", title: `${e.before} → ${e.after}`, html: EDIT },
          h("button", { type: "button", class: "label", title: "Change this", onclick: () => reopenEdit(i) },
            h("s", {}, short(e.before)), " → ", h("b", {}, short(e.after)),
          ),
          h("button", { type: "button", class: "x", "aria-label": "Remove text change", onclick: () => removeEdit(i) }, "×"),
        ),
      ),
    );
    if (recorder.active || recording) {
      marks
        .filter((m) => m.kind === "point")
        .forEach((m, n) => items.push(h("span", { class: "chip", title: m.label, html: PIN }, h("span", { class: "label" }, `${fmt(m.t)} ${friendly(m.target, n + 1)}`))));
    }
    if (recording && !recorder.active) {
      items.push(
        h("span", { class: "chip voice", html: MIC }, h("span", { class: "label" }, `Voice note ${fmt(recording.ms)}`),
          h("button", { type: "button", class: "x", "aria-label": "Discard voice note", onclick: () => ((recording = null), (marks = []), render()) }, "×"),
        ),
      );
    }
    attachments.forEach((a, i) =>
      items.push(
        h("span", { class: "thumb" }, h("img", { src: a.url, alt: "" }),
          h("button", { type: "button", class: "x", "aria-label": "Remove image", onclick: () => (URL.revokeObjectURL(a.url), attachments.splice(i, 1), render()) }, "×"),
        ),
      ),
    );
    chips.replaceChildren(...items);
    chips.hidden = !items.length;

    // Input row, or the edit box while suggesting new wording.
    editBox.hidden = !editing;
    previewBtn.hidden = !editing?.node;
    previewBtn.textContent = previewOn ? "See original" : "See change";
    previewBtn.classList.toggle("on", !previewOn);
    inputRow.hidden = !!editing;
    textarea.placeholder = recorder.active ? "You can type too…" : "What should change? What went wrong?";
    textarea.disabled = sending;
    clipBtn.hidden = attachments.length >= MAX_ATTACHMENTS;
    (clipBtn as HTMLButtonElement).disabled = sending;
    micBtn.hidden = !canRecord();
    micBtn.classList.toggle("on", recorder.active);
    micBtn.innerHTML = recorder.active ? STOP : MIC;
    micBtn.setAttribute("aria-label", recorder.active ? "Stop recording" : "Record a voice note");
    micBtn.title = recorder.active ? "Stop recording" : "Talk";
    (micBtn as HTMLButtonElement).disabled = sending;
    sendBtn.disabled = sending || !hasContent();
    sendBtn.classList.toggle("progress", sending);
    sendBtn.innerHTML = sending ? `<span>${sendStage}</span>` : SEND;

    // Footer: who's sending, the screenshot note, close.
    const name = storage(NAME_KEY);
    nameBtn.hidden = editingName;
    nameInput.hidden = !editingName;
    nameBtn.textContent = name ? `as ${name}` : "Add your name";
    const voice = voiceOnOpen();
    const key = opts.hotkey ? opts.hotkey.toUpperCase() : "";
    voiceToggle.hidden = !opts.hotkey || touch() || !canRecord() || editingName;
    voiceToggle.setAttribute("aria-checked", String(voice));
    voiceToggle.setAttribute("aria-label", `Pressing ${key} opens feedback ${voice ? "with" : "without"} voice`);
    voiceToggle.innerHTML = `<kbd>${key}</kbd><span>opens ${voice ? "with" : "without"} voice</span>`;
    voiceToggle.title = voice ? `Pressing ${key} starts recording. Click to have ${key} open it for typing only.` : `Pressing ${key} opens it for typing. Click to have ${key} start recording too.`;
    footNote.textContent = opts.screenshots ? "Screenshot included" : "";
    (closeBtn.lastChild as HTMLElement).textContent = touch() ? "" : "Esc";
    (closeBtn as HTMLButtonElement).disabled = sending;
  }

  /** The folded button: what's going on, at a glance. Recording is a red, pulsing timer, so it's unmistakable. */
  function renderMini() {
    const rec = recorder.active;
    const tagged = tags.length + marks.filter((m) => m.kind === "point").length + textEdits.length;
    mini.classList.toggle("recording", rec);
    mini.innerHTML = rec
      ? `<span class="time">${fmt(recorder.elapsed)}</span>`
      : `${recording ? MIC : ICON}${tagged ? `<span class="badge">${tagged}</span>` : ""}`;
    mini.setAttribute(
      "aria-label",
      rec ? `Recording, ${fmt(recorder.elapsed)}. Tap to finish and send` : `Feedback in progress${tagged ? `, ${tagged} tagged` : ""}. Tap to add details and send`,
    );
    placeMini();
  }

  render();
  return { open: openBar, close, host };
}

/** Say why the microphone failed, so testers (and developers reading the console) can fix it. */
function micErrorMessage(err: unknown): string {
  const e = err as DOMException | undefined;
  if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") return "No microphone was found. You can still type your feedback.";
  if (e?.name === "NotReadableError") return "Your microphone is in use by another app. Close it and try again, or type your feedback.";
  if (e?.name === "NotAllowedError" && /system/i.test(e.message))
    return "Your computer is blocking the browser from using the microphone. Allow it in your system privacy settings, or type your feedback.";
  if (e?.name === "NotAllowedError" || e?.name === "SecurityError")
    return "Microphone access is off for this site. Allow it from the icon in the address bar, or type your feedback.";
  return "Couldn't start recording. You can still type your feedback.";
}

/** True when a key press is going into a text field, so the hotkey must not fire. */
function isTyping(e: KeyboardEvent): boolean {
  const el = (e.composedPath()[0] ?? e.target) as HTMLElement | null;
  if (!el || !(el instanceof HTMLElement)) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}
